import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { SimulationObjective } from "@opus-dt/shared-types";
import { toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import type { SimulationObjectiveChoice } from "@/types/sim-sandbox.types";
import { useSandboxDraft } from "./sandbox/hooks/useSandboxDraft";
import { useCreateScenario, useCreateAutoBatch } from "./hooks";

// O caminho de entrada único do hub: escolher onde + o que se quer saber e
// simular, sem passar por nenhum painel. Cria o cenário JÁ EXECUTÁVEL (o backend semeia a
// estrutura quando recebe linha + produto), enfileira o lote e leva o usuário ao cenário com a
// simulação em andamento.
// Uma execução = UM lote (configuração atual + variações), que é o que "uma ação, duas entregas"
// exige: a resposta aparece assim que a rodada da configuração atual termina e as variações vão
// preenchendo depois.

/** Traduz a escolha da tela para os campos do contrato do lote. */
export function toBatchInput(choice: SimulationObjectiveChoice): {
  objective: SimulationObjective;
  targetQuantity?: number;
  productionOrderId?: string;
  horizonHours: number;
} {
  if (choice.kind === "OP_ON_TIME") {
    return {
      objective: "OP_ON_TIME",
      ...(choice.productionOrderId ? { productionOrderId: choice.productionOrderId } : {}),
      // Quantidade, janela e prazo saem da ordem; o teto é derivado no servidor.
      horizonHours: 24,
    };
  }
  if (choice.kind === "TIME_TO_QUANTITY") {
    return {
      objective: "TIME_TO_QUANTITY",
      // O contrato exige inteiro positivo; sem arredondar, "1500,5" digitado vira 400 cru.
      targetQuantity: Math.max(1, Math.round(choice.quantity)),
      // Quando a pergunta É o tempo não existe janela: o horizonte vira teto de segurança e quem
      // o deriva é o SERVIDOR (só ele conhece o ritmo da linha). Este valor é ignorado nesse
      // objetivo — vai só para satisfazer o contrato.
      horizonHours: 24,
    };
  }
  if (choice.kind === "TARGET_QUANTITY") {
    return {
      objective: "TARGET_QUANTITY",
      ...(choice.minQuantity != null && choice.minQuantity > 0
        ? { targetQuantity: Math.round(choice.minQuantity) }
        : {}),
      horizonHours: windowHours(choice.window.hours),
    };
  }
  return { objective: "OEE_IMPROVEMENT", horizonHours: windowHours(choice.window.hours) };
}

// A janela é digitada livremente e o contrato exige inteiro positivo: uma janela de "0,4 h" passa
// no gate da tela (> 0) e arredondaria para 0, devolvendo um 400 cru do Zod.
function windowHours(hours: number): number {
  return Math.min(720, Math.max(1, Math.round(hours)));
}

interface StartInput {
  lineId: string;
  lineName: string;
  productId: string;
  objective: SimulationObjectiveChoice;
}

export function useStartSimulation() {
  const navigate = useNavigate();
  const createScenario = useCreateScenario();
  const createBatch = useCreateAutoBatch();

  const start = useCallback(
    async (input: StartInput) => {
      let scenarioId: string | null = null;
      try {
        useSandboxDraft.getState().reset();
        const scenario = await createScenario.mutateAsync({
          lineId: input.lineId,
          base: "CURRENT_LINE",
          name: `E se… ${input.lineName}`,
          productId: input.productId,
        });
        scenarioId = scenario.id;
        const batch = await createBatch.mutateAsync({
          scenarioId: scenario.id,
          ...toBatchInput(input.objective),
          variationPct: 10,
          candidateCount: 6,
        });
        // A resposta vive na tela do LOTE (progresso ao vivo + recomendação). O sandbox não tem
        // superfície para o lote, então mandar o usuário para lá terminava numa tela muda.
        navigate(`/simulations/auto/${batch.id}`);
      } catch (err) {
        // O cenário pode ter sido criado antes de a simulação falhar (ex.: já há uma rodando na
        // organização). Levar o usuário até ele é melhor do que descartar o trabalho em silêncio.
        if (scenarioId) {
          toast.error("Cenário criado, mas a simulação não iniciou.", {
            description: getFriendlyErrorMessage(err),
          });
          navigate(`/simulations/sandbox/${scenarioId}`);
          return;
        }
        toast.error("Não foi possível iniciar a simulação.", {
          description: getFriendlyErrorMessage(err),
        });
      }
    },
    [createScenario, createBatch, navigate]
  );

  return { start, isPending: createScenario.isPending || createBatch.isPending };
}
